type Iteratee<T> = ((item: T) => unknown) | keyof T;

const resolve = <T>(iteratee: Iteratee<T>, item: T): unknown =>
    typeof iteratee === 'function' ? iteratee(item) : item[iteratee];

// Mirrors lodash's ascending comparison: `undefined`/`null` sort last.
export const compareAscending = (a: unknown, b: unknown): number => {
    if (a === b) return 0;
    if (a === undefined || a === null) return 1;
    if (b === undefined || b === null) return -1;
    return (a as number) < (b as number) ? -1 : 1;
};

/**
 * Native replacement for lodash/fp `sortBy`. Returns a new array sorted in
 * ascending order by `iteratee` (a function or a property key). Stable and
 * non-mutating.
 *
 * The `iteratee` is evaluated exactly once per element (a Schwartzian
 * transform), matching lodash. This matters both for performance and because
 * some call sites rely on the iteratee being invoked for every element — a
 * plain `.sort(comparator)` would skip it entirely for a single-element array
 * and re-invoke it for every comparison otherwise.
 */
export default function sortBy<T>(collection: readonly T[] | null | undefined, iteratee: Iteratee<T>): T[] {
    return [...(collection ?? [])]
        .map((item, index) => ({ item, index, key: resolve(iteratee, item) }))
        .sort((a, b) => compareAscending(a.key, b.key) || a.index - b.index)
        .map(entry => entry.item);
}
